import React from 'react';

import { Typography } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

import ColoredButton from 'components/ColoredButton';
import type { AssetProcessingActivityReviewRow, AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import type { AssetRopaCapabilities } from 'pages/ProcessingActivities/_shared/assetRopaCapabilities';

import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

import AssetProcessingActivityReviewBoxList from './AssetProcessingActivityReviewBoxList';
import BulkAcceptFooter from './BulkAcceptFooter';

type Props = {
  asset: AssetRopaReview;
  bulkAcceptLoading?: boolean;
  canConfirmNoneApply?: boolean;
  capabilities: AssetRopaCapabilities;
  onAcceptRow: (row: AssetProcessingActivityReviewRow) => void;
  onAddProcessingActivity: (asset: AssetRopaReview) => void;
  onAssessDpia: (row: AssetProcessingActivityReviewRow) => void;
  onBulkAccept: (asset: AssetRopaReview) => void;
  onConfirmNoneApply?: (asset: AssetRopaReview) => void;
  onOpenAdminDpiaReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenAdminReview: (row: AssetProcessingActivityReviewRow) => void;
  onOpenOwnerEditor: (row: AssetProcessingActivityReviewRow, asset: AssetRopaReview, mode: 'view' | 'edit') => void;
  onRemoveProcessingActivity: (row: AssetProcessingActivityReviewRow, asset: AssetRopaReview) => void;
  onRequestClarification: (row: AssetProcessingActivityReviewRow) => void;
  reviewLocked?: boolean;
};

const AssetRopaExpandedAssetSection: React.FC<Props> = ({
  asset,
  bulkAcceptLoading = false,
  canConfirmNoneApply = false,
  capabilities,
  onAcceptRow,
  onAddProcessingActivity,
  onAssessDpia,
  onBulkAccept,
  onConfirmNoneApply,
  onOpenAdminDpiaReview,
  onOpenAdminReview,
  onOpenOwnerEditor,
  onRemoveProcessingActivity,
  onRequestClarification,
  reviewLocked = false,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const hasProcessingActivities = asset.processingActivityReviews.length > 0;
  const canAddProcessingActivity = capabilities.canAddProcessingActivity && !reviewLocked;
  const showConfirmNoneApply = !hasProcessingActivities && canConfirmNoneApply && Boolean(onConfirmNoneApply);

  const tableEmptyState = (
    <>
      <Typography variant='h6'>{t(processing_activities_messages.asset_ropa.panel.no_processing_activities_title)}</Typography>
      <Typography color='textSecondary'>{t(processing_activities_messages.asset_ropa.panel.no_processing_activities_body)}</Typography>
    </>
  );

  const showPanelActionsArea = canAddProcessingActivity || showConfirmNoneApply || capabilities.showBulkAcceptFooter;

  return (
    <div className={styles.reviewAccordionAssetContent}>
      <AssetProcessingActivityReviewBoxList
        capabilities={capabilities}
        emptyStateContent={tableEmptyState}
        onAccept={onAcceptRow}
        onAssessDpia={onAssessDpia}
        onOpenAdminDpiaReview={onOpenAdminDpiaReview}
        onOpenAdminReview={onOpenAdminReview}
        onOpenOwnerEditor={(row, mode) => onOpenOwnerEditor(row, asset, mode)}
        onRemove={row => onRemoveProcessingActivity(row, asset)}
        onRequestClarification={onRequestClarification}
        rows={asset.processingActivityReviews}
      />
      {showPanelActionsArea && (
        <div className={styles.reviewAccordionActions}>
          {(canAddProcessingActivity || showConfirmNoneApply) && (
            <div className={styles.panelActions}>
              {canAddProcessingActivity && (
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled={false}
                  icon={false}
                  onClick={() => onAddProcessingActivity(asset)}
                  showLoader={false}
                  startIcon={<AddIcon />}
                  textLike={false}
                  variant='outlined'
                >
                  {t(processing_activities_messages.asset_ropa.actions.add_processing_activity)}
                </ColoredButton>
              )}
              {showConfirmNoneApply && onConfirmNoneApply && (
                <ColoredButton
                  className=''
                  customColor='grey'
                  disabled={false}
                  icon={false}
                  onClick={() => onConfirmNoneApply(asset)}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  {t(processing_activities_messages.asset_ropa.actions.confirm_none_apply)}
                </ColoredButton>
              )}
            </div>
          )}
          {capabilities.showBulkAcceptFooter && <BulkAcceptFooter asset={asset} loading={bulkAcceptLoading} onBulkAccept={onBulkAccept} />}
        </div>
      )}
    </div>
  );
};

export default AssetRopaExpandedAssetSection;
